import {
  WifiIcon,
  BoltIcon,
  LightBulbIcon,
  SunIcon,
  HomeModernIcon,
  BeakerIcon,
  TableCellsIcon,
  UserIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  PresentationChartBarIcon,
  PhoneIcon,
  VideoCameraIcon,
  PrinterIcon,
  LifebuoyIcon,
  Battery100Icon,
  FireIcon,
  ShoppingBagIcon,
  BuildingStorefrontIcon,
  SparklesIcon,
  EyeSlashIcon,
  HeartIcon,
  LockClosedIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  CogIcon,
  DocumentTextIcon,
  SpeakerWaveIcon,
  EnvelopeIcon,
  MapPinIcon,
  TruckIcon,
  CubeIcon,
  CreditCardIcon,
  MusicalNoteIcon,
  FilmIcon,
  CalendarIcon,
  CloudIcon,
  ArchiveBoxIcon,
  ArrowUpIcon,
  ViewfinderCircleIcon,
  ClipboardDocumentListIcon,
  ServerIcon,
  ArrowDownTrayIcon,
  Battery50Icon,
  ShieldCheckIcon,
  DocumentCheckIcon,
  CurrencyDollarIcon,
  ClockIcon,
  GlobeAltIcon,
  CubeTransparentIcon,
  AdjustmentsHorizontalIcon,
  HomeIcon,
  CalendarDaysIcon,
  MegaphoneIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  BuildingLibraryIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

// Lucide icons for amenities not available in Heroicons
import { Snowflake, Coffee, Headphones, Accessibility } from "lucide-react";

const amenities = [
  // Basic Infrastructure (updated with Heroicons)
  {
    value: 1,
    title: "High-speed internet/Wi-Fi",
    hoverDescription: "Reliable high-speed connectivity",
    icon: WifiIcon, // Heroicon
    category: "Basic Infrastructure",
  },
  {
    value: 2,
    title: "Electrical outlets/USB ports",
    hoverDescription: "Ample power access throughout",
    icon: BoltIcon, // Heroicon (better than FaPlug)
    category: "Basic Infrastructure",
  },
  {
    value: 3,
    title: "Lighting (natural & artificial)",
    hoverDescription: "Well-lit workspace",
    icon: LightBulbIcon, // Heroicon
    category: "Basic Infrastructure",
  },
  {
    value: 4,
    title: "HVAC systems",
    hoverDescription: "Climate controlled environment",
    icon: Snowflake, // Lucide (HVAC/cooling)
    category: "Basic Infrastructure",
  },
  {
    value: 5,
    title: "Restrooms",
    hoverDescription: "Clean, well-maintained facilities",
    icon: HomeModernIcon, // Heroicon (building representation)
    category: "Basic Infrastructure",
  },
  {
    value: 6,
    title: "Drinking water",
    hoverDescription: "Filtered water stations",
    icon: BeakerIcon, // Heroicon (water/beaker representation)
    category: "Basic Infrastructure",
  },

  // Furniture & Workstations (updated)
  {
    value: 7,
    title: "Adjustable desks",
    hoverDescription: "Sit-stand workstations",
    icon: TableCellsIcon, // Heroicon (table representation)
    category: "Furniture",
  },
  {
    value: 8,
    title: "Ergonomic chairs",
    hoverDescription: "Supportive seating",
    icon: UserIcon, // Heroicon (person in chair representation)
    category: "Furniture",
  },
  {
    value: 9,
    title: "Private offices",
    hoverDescription: "Dedicated workspaces",
    icon: BuildingOfficeIcon, // Heroicon
    category: "Furniture",
  },
  {
    value: 10,
    title: "Open-plan workstations",
    hoverDescription: "Collaborative seating",
    icon: UserGroupIcon, // Heroicon
    category: "Furniture",
  },
  {
    value: 11,
    title: "Lounge seating",
    hoverDescription: "Comfortable casual areas",
    icon: ChatBubbleLeftRightIcon, // Heroicon (conversation/lounge)
    category: "Furniture",
  },

  // Meeting & Collaboration Spaces (updated)
  {
    value: 12,
    title: "Conference rooms",
    hoverDescription: "Bookable meeting spaces",
    icon: PresentationChartBarIcon, // Heroicon
    category: "Meeting Spaces",
  },
  {
    value: 13,
    title: "Phone booths",
    hoverDescription: "Soundproof call pods",
    icon: PhoneIcon, // Heroicon
    category: "Meeting Spaces",
  },
  {
    value: 14,
    title: "Whiteboards",
    hoverDescription: "Collaboration tools",
    icon: PresentationChartBarIcon, // Heroicon (same as conference - presentation)
    category: "Meeting Spaces",
  },
  {
    value: 15,
    title: "Projectors",
    hoverDescription: "Presentation equipment",
    icon: VideoCameraIcon, // Heroicon (video/projection)
    category: "Meeting Spaces",
  },

  // Technology & Equipment (updated)
  {
    value: 16,
    title: "Printers/Scanners",
    hoverDescription: "Shared office equipment",
    icon: PrinterIcon, // Heroicon
    category: "Technology",
  },
  {
    value: 17,
    title: "IT support",
    hoverDescription: "Technical assistance",
    icon: LifebuoyIcon, // Heroicon (support/help)
    category: "Technology",
  },
  {
    value: 18,
    title: "Video conferencing",
    hoverDescription: "Meeting technology",
    icon: VideoCameraIcon, // Heroicon
    category: "Technology",
  },
  {
    value: 19,
    title: "Charging stations",
    hoverDescription: "Device power hubs",
    icon: Battery100Icon, // Heroicon
    category: "Technology",
  },

  // Kitchen & Break Areas (updated)
  {
    value: 20,
    title: "Coffee station",
    hoverDescription: "Complimentary beverages",
    icon: Coffee, // Lucide (coffee/beverages)
    category: "Kitchen",
  },
  {
    value: 21,
    title: "Microwave & fridge",
    hoverDescription: "Food prep appliances",
    icon: FireIcon, // Heroicon (stove/heat for microwave)
    category: "Kitchen",
  },
  {
    value: 22,
    title: "Snack bar",
    hoverDescription: "Refreshment options",
    icon: ShoppingBagIcon, // Heroicon
    category: "Kitchen",
  },
  {
    value: 23,
    title: "Dining area",
    hoverDescription: "Designated eating space",
    icon: BuildingStorefrontIcon, // Heroicon (restaurant representation)
    category: "Kitchen",
  },

  // Wellness & Comfort (updated)
  {
    value: 24,
    title: "Natural lighting",
    hoverDescription: "Window access",
    icon: SunIcon, // Heroicon
    category: "Wellness",
  },
  {
    value: 25,
    title: "Air purification",
    hoverDescription: "Clean air systems",
    icon: SparklesIcon, // Heroicon (clean/sparkle)
    category: "Wellness",
  },
  {
    value: 26,
    title: "Quiet rooms",
    hoverDescription: "Focus spaces",
    icon: EyeSlashIcon, // Heroicon (quiet/no eyes)
    category: "Wellness",
  },
  {
    value: 27,
    title: "Nursing room",
    hoverDescription: "Parent care space",
    icon: HeartIcon, // Heroicon (care/health)
    category: "Wellness",
  },

  // Security & Safety (updated)
  {
    value: 28,
    title: "24/7 access",
    hoverDescription: "Secure entry",
    icon: LockClosedIcon, // Heroicon
    category: "Security",
  },
  {
    value: 29,
    title: "Surveillance",
    hoverDescription: "Security cameras",
    icon: EyeIcon, // Heroicon
    category: "Security",
  },
  {
    value: 30,
    title: "Fire safety",
    hoverDescription: "Emergency systems",
    icon: ExclamationTriangleIcon, // Heroicon (warning/safety)
    category: "Security",
  },
  {
    value: 31,
    title: "First aid",
    hoverDescription: "Medical supplies",
    icon: PlusCircleIcon, // Heroicon (medical cross representation)
    category: "Security",
  },

  // Accessibility Features (updated)
  {
    value: 32,
    title: "Wheelchair access",
    hoverDescription: "ADA compliant",
    icon: CogIcon, // Heroicon (mechanical/access representation)
    category: "Accessibility",
  },
  {
    value: 33,
    title: "Braille signage",
    hoverDescription: "Tactile indicators",
    icon: DocumentTextIcon, // Heroicon (text/document)
    category: "Accessibility",
  },
  {
    value: 34,
    title: "Assistive listening",
    hoverDescription: "Hearing support",
    icon: SpeakerWaveIcon, // Heroicon (sound/speaker)
    category: "Accessibility",
  },

  // Office Services (updated)
  {
    value: 35,
    title: "Mail handling",
    hoverDescription: "Package reception",
    icon: EnvelopeIcon, // Heroicon
    category: "Office Services",
  },
  {
    value: 36,
    title: "Business address",
    hoverDescription: "Professional location",
    icon: MapPinIcon, // Heroicon
    category: "Office Services",
  },

  // Industrial & Warehouse (updated)
  {
    value: 37,
    title: "Loading docks",
    hoverDescription: "Freight access",
    icon: TruckIcon, // Heroicon
    category: "Industrial",
  },
  {
    value: 38,
    title: "Forklifts",
    hoverDescription: "Material handling",
    icon: CubeIcon, // Heroicon (cargo/box representation)
    category: "Industrial",
  },

  // Retail & Storefront (updated)
  {
    value: 39,
    title: "POS systems",
    hoverDescription: "Payment processing",
    icon: CreditCardIcon, // Heroicon
    category: "Retail",
  },

  // Studios (updated)
  {
    value: 40,
    title: "Soundproofing",
    hoverDescription: "Acoustic treatment",
    icon: MusicalNoteIcon, // Heroicon (sound/music)
    category: "Studios",
  },
  {
    value: 41,
    title: "Studio lighting",
    hoverDescription: "Professional rigs",
    icon: FilmIcon, // Heroicon (film production)
    category: "Studios",
  },

  // Medical & Healthcare (updated)
  {
    value: 42,
    title: "Exam rooms",
    hoverDescription: "Patient care spaces",
    icon: HeartIcon, // Heroicon (same as nursing room)
    category: "Medical",
  },

  // Laboratories (updated)
  {
    value: 43,
    title: "Lab benches",
    hoverDescription: "Research workstations",
    icon: BeakerIcon, // Heroicon
    category: "Labs",
  },

  // Co-Working & Shared (updated)
  {
    value: 44,
    title: "Community events",
    hoverDescription: "Networking opportunities",
    icon: CalendarIcon, // Heroicon (events on calendar)
    category: "Community",
  },
  {
    value: 45,
    title: "Pet-friendly",
    hoverDescription: "Animal companions welcome",
    icon: HeartIcon, // Heroicon (love for pets)
    category: "Community",
  },

  // Home Office (updated)
  {
    value: 46,
    title: "Noise-canceling",
    hoverDescription: "Focus solutions",
    icon: Headphones, // Lucide (noise-canceling headphones)
    category: "Home Office",
  },

  // Outdoor Workspaces (updated)
  {
    value: 47,
    title: "Rooftop terraces",
    hoverDescription: "Open-air workspaces",
    icon: CloudIcon, // Heroicon (high up/clouds)
    category: "Outdoor",
  },
  {
    value: 48,
    title: "Beachside setups",
    hoverDescription: "Casual work environments",
    icon: SunIcon, // Heroicon (beach sun)
    category: "Outdoor",
  },
  {
    value: 49,
    title: "Receptionist",
    hoverDescription: "Shared front desk reception",
    icon: UserIcon, // Heroicon
    category: "Community",
  },
  {
    value: 50,
    title: "Storage",
    hoverDescription: "Private storage spaces/lockers",
    icon: ArchiveBoxIcon, // Heroicon
    category: "Community",
  },
  {
    value: 51,
    title: "Elevator access",
    hoverDescription: "Elevator access to all floors",
    icon: ArrowUpIcon, // Heroicon (up arrow for elevator)
    category: "Community",
  },
  {
    value: 52,
    title: "Car parking",
    hoverDescription: "Designated parking spaces",
    icon: MapPinIcon, // Heroicon (location pin for parking)
    category: "Community",
  },
  {
    value: 53,
    title: "Bike storage",
    hoverDescription: "Storage space for bicycles/motorcycles",
    icon: CogIcon, // Heroicon (mechanical/gears for bikes)
    category: "Community",
  },
  {
    value: 54,
    title: "Shower facilities",
    hoverDescription: "Shared shower facilities",
    icon: SparklesIcon, // Heroicon (clean/sparkle for shower)
    category: "Office Cleaning",
  },
  {
    value: 55,
    title: "Disabled access",
    hoverDescription: "Wheelchair accessible",
    icon: Accessibility, // Lucide (wheelchair/accessibility)
    category: "Office Cleaning",
  },
  {
    value: 56,
    title: "Office cleaning",
    hoverDescription: "Shared cleaning services for all spaces",
    icon: HomeModernIcon, // Heroicon (clean building)
    category: "Office Cleaning",
  },

  // NEW COMMERCIAL AMENITIES (Heroicons preferred)

  // Industrial Amenities
  {
    value: 57,
    title: "Heavy Power Supply",
    hoverDescription: "480V 3-phase power available",
    icon: BoltIcon, // Heroicon
    category: "Industrial",
    commercialGrade: true,
  },
  {
    value: 58,
    title: "Compressed Air Lines",
    hoverDescription: "Shop air system installed",
    icon: CogIcon, // Heroicon (mechanical/industrial)
    category: "Industrial",
    commercialGrade: true,
  },
  {
    value: 59,
    title: "Gas Lines",
    hoverDescription: "Natural gas connections",
    icon: FireIcon, // Heroicon (fire/flame for gas)
    category: "Industrial",
    commercialGrade: true,
  },
  {
    value: 60,
    title: "Generator/Backup Power",
    hoverDescription: "Backup power systems",
    icon: Battery50Icon, // Heroicon
    category: "Industrial",
    commercialGrade: true,
  },

  // Retail Specific
  {
    value: 61,
    title: "Storefront Signage",
    hoverDescription: "Exterior signage rights included",
    icon: BuildingStorefrontIcon, // Heroicon
    category: "Retail",
    commercialGrade: true,
  },
  {
    value: 62,
    title: "Display Windows",
    hoverDescription: "Street-facing display areas",
    icon: ViewfinderCircleIcon, // Heroicon (view/window)
    category: "Retail",
    commercialGrade: true,
  },

  // Professional Services
  {
    value: 63,
    title: "Notary Services",
    hoverDescription: "On-site notary available",
    icon: DocumentTextIcon, // Heroicon (document/legal)
    category: "Office Services",
    commercialGrade: true,
  },
  {
    value: 64,
    title: "Secretarial Services",
    hoverDescription: "Administrative support",
    icon: ClipboardDocumentListIcon, // Heroicon (clipboard/administrative)
    category: "Office Services",
    commercialGrade: true,
  },

  // Logistics & Transportation
  {
    value: 65,
    title: "Rail Access",
    hoverDescription: "Direct rail line access",
    icon: ServerIcon, // Heroicon (rail/track representation)
    category: "Industrial",
    commercialGrade: true,
  },
  {
    value: 66,
    title: "Loading Dock Levelers",
    hoverDescription: "Hydraulic dock levelers",
    icon: ArrowDownTrayIcon, // Heroicon (loading/arrow down)
    category: "Industrial",
    commercialGrade: true,
  },
  {
    value: 67,
    title: "Cross-Docking",
    hoverDescription: "Cross-dock facilities",
    icon: CubeTransparentIcon, // Heroicon
    category: "Industrial",
    commercialGrade: true,
  },

  // Security Enhancements
  {
    value: 68,
    title: "Security Guards",
    hoverDescription: "24/7 on-site security personnel",
    icon: ShieldCheckIcon, // Heroicon
    category: "Security",
    commercialGrade: true,
  },
  {
    value: 69,
    title: "Access Control Systems",
    hoverDescription: "Keycard/biometric entry",
    icon: LockClosedIcon, // Heroicon
    category: "Security",
    commercialGrade: true,
  },

  // Professional & Business Services
  {
    value: 70,
    title: "Lease Administration",
    hoverDescription: "Full lease management services",
    icon: DocumentCheckIcon, // Heroicon
    category: "Office Services",
    commercialGrade: true,
  },
  {
    value: 71,
    title: "Investment Grade",
    hoverDescription: "Premium commercial real estate",
    icon: CurrencyDollarIcon, // Heroicon
    category: "Office Services",
    commercialGrade: true,
  },

  // Building Features
  {
    value: 72,
    title: "24/7 Staffed",
    hoverDescription: "Round-the-clock staffing",
    icon: ClockIcon, // Heroicon
    category: "Basic Infrastructure",
    commercialGrade: true,
  },
  {
    value: 73,
    title: "Fiber Optic Internet",
    hoverDescription: "Enterprise-grade connectivity",
    icon: GlobeAltIcon, // Heroicon
    category: "Technology",
    commercialGrade: true,
  },
  {
    value: 74,
    title: "Climate Control",
    hoverDescription: "Precision temperature control",
    icon: AdjustmentsHorizontalIcon, // Heroicon (controls/adjustments)
    category: "Basic Infrastructure",
    commercialGrade: true,
  },
  {
    value: 75,
    title: "Building Management System",
    hoverDescription: "Automated building controls",
    icon: Cog6ToothIcon, // Heroicon
    category: "Technology",
    commercialGrade: true,
  },

  // Specialized Spaces
  {
    value: 76,
    title: "Live-Work Units",
    hoverDescription: "Combined residential and commercial",
    icon: HomeIcon, // Heroicon
    category: "Mixed-Use",
    commercialGrade: true,
  },
  {
    value: 77,
    title: "Event Space Rental",
    hoverDescription: "Rentable event venues",
    icon: CalendarDaysIcon, // Heroicon
    category: "Community",
    commercialGrade: true,
  },

  // Business Support
  {
    value: 78,
    title: "Marketing Support",
    hoverDescription: "Promotional assistance",
    icon: MegaphoneIcon, // Heroicon
    category: "Office Services",
    commercialGrade: true,
  },
  {
    value: 79,
    title: "Business Analytics",
    hoverDescription: "Performance reporting tools",
    icon: ChartBarIcon, // Heroicon
    category: "Technology",
    commercialGrade: true,
  },

  // Location & Area Features
  {
    value: 80,
    title: "Corporate Campus",
    hoverDescription: "Multi-building campus setting",
    icon: BuildingLibraryIcon, // Heroicon
    category: "Location",
    commercialGrade: true,
  },
  {
    value: 81,
    title: "High Growth Area",
    hoverDescription: "Located in expanding business district",
    icon: ArrowTrendingUpIcon, // Heroicon
    category: "Location",
    commercialGrade: true,
  },

  // Sustainability
  {
    value: 82,
    title: "Green Building Certified",
    hoverDescription: "LEED or equivalent certification",
    icon: CheckCircleIcon, // Heroicon (certification/check)
    category: "Sustainability",
    commercialGrade: true,
  },
  {
    value: 83,
    title: "Solar Power",
    hoverDescription: "On-site solar energy generation",
    icon: SunIcon, // Heroicon
    category: "Sustainability",
    commercialGrade: true,
  },
  {
    value: 84,
    title: "EV Charging Stations",
    hoverDescription: "Electric vehicle charging",
    icon: BoltIcon, // Heroicon
    category: "Sustainability",
    commercialGrade: true,
  },

  // Industry Specific
  {
    value: 85,
    title: "Clean Rooms",
    hoverDescription: "Contamination-controlled environments",
    icon: SparklesIcon, // Heroicon
    category: "Specialized",
    commercialGrade: true,
  },
  {
    value: 86,
    title: "Wet Labs",
    hoverDescription: "Laboratories with plumbing",
    icon: BeakerIcon, // Heroicon
    category: "Labs",
    commercialGrade: true,
  },
  {
    value: 87,
    title: "Broadcast Facilities",
    hoverDescription: "TV/radio broadcasting equipment",
    icon: VideoCameraIcon, // Heroicon
    category: "Studios",
    commercialGrade: true,
  },
];

// Optional: Group amenities by category for UI organization
export const amenitiesByCategory = {
  "Basic Infrastructure": [1, 2, 3, 4, 5, 6, 72, 74],
  Furniture: [7, 8, 9, 10, 11],
  "Meeting Spaces": [12, 13, 14, 15],
  Technology: [16, 17, 18, 19, 73, 75, 79],
  Kitchen: [20, 21, 22, 23],
  Wellness: [24, 25, 26, 27],
  Security: [28, 29, 30, 31, 68, 69],
  Accessibility: [32, 33, 34, 55],
  "Office Services": [35, 36, 63, 64, 70, 71, 78],
  Industrial: [37, 38, 57, 58, 59, 60, 65, 66, 67],
  Retail: [39, 61, 62],
  Studios: [40, 41, 87],
  Medical: [42],
  Labs: [43, 86],
  Community: [44, 45, 49, 50, 51, 52, 53, 77],
  "Home Office": [46],
  Outdoor: [47, 48],
  "Office Cleaning": [54, 56],
  Location: [80, 81],
  Sustainability: [82, 83, 84],
  Specialized: [76, 85],
  "Mixed-Use": [76],
};

// Helper function to get icon component
// Helper function to get icon component
export const getAmenityIcon = (value: number) => {
  const amenity = amenities.find((a) => a.value === value);
  return amenity ? amenity.icon : null;
};

export default amenities;
